import { useState } from "react";
import type { FocusEvent, KeyboardEvent } from "react";

import type {
  DistribuicaoArvoreProps,
  DistribuicaoArvoreViewModel,
  LinhaArvore,
  NoArvore,
} from "./types-distribuicao-arvore";

function achatar(
  nos: NoArvore[],
  expandidos: ReadonlySet<string>,
  nivel = 0,
): Omit<LinhaArvore, "focavel" | "testId" | "toggleTestId">[] {
  return nos.flatMap((no, indice) => {
    const temFilhos = no.filhos.length > 0;
    const expandido = temFilhos && expandidos.has(no.id);
    const linha = { no, nivel, temFilhos, expandido, posicao: indice + 1, irmaos: nos.length };
    return expandido ? [linha, ...achatar(no.filhos, expandidos, nivel + 1)] : [linha];
  });
}

function focar(evento: KeyboardEvent<HTMLElement>, id: string) {
  const arvore = evento.currentTarget.closest('[role="tree"]');
  arvore?.querySelector<HTMLElement>(`[data-no-id="${id}"]`)?.focus();
}

export function useDistribuicaoArvore({
  nos,
  mobile = false,
}: DistribuicaoArvoreProps): DistribuicaoArvoreViewModel {
  const [expandidos, setExpandidos] = useState<ReadonlySet<string>>(new Set());
  const [focoId, setFocoId] = useState<string | null>(null);

  const visiveis = achatar(nos, expandidos);
  const idFocavel = visiveis.some(({ no }) => no.id === focoId) ? focoId : visiveis[0]?.no.id;
  const linhas = visiveis.map((linha) => ({
    ...linha,
    focavel: linha.no.id === idFocavel,
    testId: `distribuicao-no-${linha.no.id}`,
    toggleTestId: `distribuicao-toggle-${linha.no.id}`,
  }));

  let parteFixa = false;
  let coletorAMostra = false;
  for (const { nivel, no } of visiveis) {
    if (nivel === 1) parteFixa = no.fixo === true;
    if (nivel >= 2 && !parteFixa) coletorAMostra = true;
  }
  let variante: DistribuicaoArvoreViewModel["variante"] = "desktop";
  if (mobile) variante = coletorAMostra ? "mobile-ampliada" : "mobile";

  const definir = (id: string, aberto: boolean) =>
    setExpandidos((atuais) => {
      const proximos = new Set(atuais);
      if (aberto) proximos.add(id);
      else proximos.delete(id);
      return proximos;
    });

  const alternar = (id: string) => definir(id, !expandidos.has(id));

  const aoTeclar = (evento: KeyboardEvent<HTMLElement>, id: string) => {
    if (evento.target !== evento.currentTarget) return;
    const indice = linhas.findIndex(({ no }) => no.id === id);
    const linha = linhas[indice];
    if (!linha) return;

    const irPara = (alvo: LinhaArvore | undefined) => {
      if (alvo) focar(evento, alvo.no.id);
    };

    switch (evento.key) {
      case "ArrowDown":
        irPara(linhas[indice + 1]);
        break;
      case "ArrowUp":
        irPara(linhas[indice - 1]);
        break;
      case "Home":
        irPara(linhas[0]);
        break;
      case "End":
        irPara(linhas[linhas.length - 1]);
        break;
      case "ArrowRight":
        if (linha.temFilhos && !linha.expandido) definir(id, true);
        else if (linha.expandido) irPara(linhas[indice + 1]);
        break;
      case "ArrowLeft":
        if (linha.expandido) definir(id, false);
        else
          irPara(
            linhas
              .slice(0, indice)
              .reverse()
              .find((anterior) => anterior.nivel === linha.nivel - 1),
          );
        break;
      case "Enter":
      case " ":
        if (linha.temFilhos) alternar(id);
        break;
      default:
        return;
    }
    evento.preventDefault();
  };

  const aoFocar = (evento: FocusEvent<HTMLElement>, id: string) => {
    if (evento.target === evento.currentTarget) setFocoId(id);
  };

  return {
    linhas,
    variante,
    alternar,
    aoFocar,
    aoTeclar,
  };
}
